import React from 'react';
import { StyleSheet, css } from 'aphrodite';

export enum BarSize { Normal, Small }

interface ProgressBarProps extends React.HTMLProps<HTMLDivElement> {
    percentage: number;
    barSize?: BarSize;
    error?: boolean;
    smooth?: boolean;
}

interface ProgressBarState {
    starting: number;
}

export class ProgressBar extends React.Component<ProgressBarProps, ProgressBarState> {
    public static getDerivedStateFromProps(nextProps: ProgressBarProps, prevState: ProgressBarState) {
        if (nextProps.smooth && (nextProps.percentage !== prevState.starting)) {
            return { starting: nextProps.percentage};
        } else {
            return null;
        }
    }

    private progressRef: React.RefObject<HTMLDivElement>;
    private smoothLoop: NodeJS.Timeout | null = null;

    constructor(props: ProgressBarProps) {
        super(props);
        this.state = { starting: this.props.percentage };
        this.progressRef = React.createRef();
        this.smoothProgress = this.smoothProgress.bind(this);
    }

    public componentDidUpdate(prevProps: ProgressBarProps) {
        if (this.props.smooth && (prevProps.percentage !== this.props.percentage)) {
            if (this.smoothLoop) {
                clearInterval(this.smoothLoop);
                this.smoothLoop = null;
            }
            this.smoothProgress(prevProps.percentage);
        }
    }

    public componentWillUnmount() {
        if (this.smoothLoop) {
            clearInterval(this.smoothLoop);
            this.smoothLoop = null;
        }
    }

    public render() {
        const { barSize, error, percentage, smooth } = this.props;
        const { starting } = this.state;

        const barSizeClass = (barSize === BarSize.Small) ? styles.small : styles.normal;
        return (
            <div className={css(styles.progressBar, barSizeClass, error && styles.error)}>
                <div
                    ref={this.progressRef}
                    className={css(styles.filler)}
                    style={{ width: smooth ? `${starting}` : `${percentage}%` }}
                />
            </div>
        );
    }

    private smoothProgress(starting: number) {
        const { percentage } = this.props;
        const duration = 30000;
        const period = 100;
        const chunk = (percentage - starting) / duration * period;
        let progress = starting;

        this.smoothLoop = setInterval(() => {
            if (progress < percentage) {
                progress = progress + chunk;
                if (this.progressRef.current) {
                    this.progressRef.current.style.width = `${progress}%`;
                }
            } else {
                if (this.smoothLoop) {
                    clearInterval(this.smoothLoop);
                    this.smoothLoop = null;
                }
            }
        }, period);
    }
}

const styles = StyleSheet.create({
    progressBar: {
        position: 'relative',
        width: 'auto',
        borderRadius: '4.5px',
        backgroundColor: '#d8d8d8',
    },
    normal: {
        height: '9px',
    },
    small: {
        height: '4px',
    },
    filler: {
        height: '100%',
        backgroundColor: '#00b67a',
        borderRadius: 'inherit',
        transition: 'width .2s ease-in',
    },
    error: {
        backgroundColor: '#FC4C5D',
    }
});
